import { useQuery } from '@apollo/client';
import { pathEq } from 'ramda';

import { TeamMembersRequestStatus } from '__generated__/GQL';
import mergeConnectionResultsBy from 'common/graphql/mergeConnectionResultsBy';
import useCompanyContext from 'common/hooks/useCompanyContext';

import {
  BankTransferRequestsListItemRequestFragment,
  ManagedUserBankTransferRequestsDocument,
  ManagedUserBankTransferRequestsQuery,
} from './managedUserBankTransferRequests.gql';

export type BankTransferRequestListPageInfo =
  ManagedUserBankTransferRequestsQuery['viewer']['company']['teamMembersRequests']['pageInfo'];

export const useManagedUserBankTransferRequests = (
  status: TeamMembersRequestStatus[],
) => {
  const { companyProfileId, companyUserId } = useCompanyContext();

  const { data, fetchMore, previousData, ...hook } = useQuery(
    ManagedUserBankTransferRequestsDocument,
    {
      notifyOnNetworkStatusChange: true,
      skip: !companyProfileId || !companyUserId,
      variables: {
        after: null,
        companyProfileId: companyProfileId ?? '',
        requesterCompanyId: companyUserId ?? '',
        status,
      },
    },
  );

  const loadMore = () => {
    return fetchMore({
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const isEqual = pathEq(
          previousResult,
          ['viewer', 'company', 'teamMembersRequests', 'pageInfo'],
          fetchMoreResult,
        );
        if (isEqual) {
          return previousResult;
        }
        return mergeConnectionResultsBy('teamMembersRequestId')(
          previousResult,
          fetchMoreResult,
        );
      },
      variables: {
        after: data?.viewer.company.teamMembersRequests.pageInfo.nextCursor,
      },
    });
  };

  const bankTransferRequests =
    data?.viewer.company.teamMembersRequests.edges.map(
      (edge) => edge.node as BankTransferRequestsListItemRequestFragment,
    ) ?? [];

  const pageInfo =
    data?.viewer.company.teamMembersRequests.pageInfo ??
    ({
      __typename: 'TeamMembersRequestsPageInfo',
      hasNextPage: false,
      nextCursor:
        previousData?.viewer.company.teamMembersRequests.pageInfo.nextCursor ??
        null,
      totalCount:
        previousData?.viewer.company.teamMembersRequests.pageInfo.totalCount ??
        0,
      totalCountInCompany:
        previousData?.viewer.company.teamMembersRequests.pageInfo
          .totalCountInCompany ?? 0,
    } satisfies BankTransferRequestListPageInfo);

  return { bankTransferRequests, loadMore, pageInfo, ...hook } as const;
};
